import { useEffect, useState, useRef } from 'react';
import type { MutableRefObject } from 'react';
import type { CallConnectionState, CallSession, CallUiState } from '@domain/call/types';
import type { UserSearchResult } from '@domain/contacts/types';
import type { ContactDirectoryUser } from '@domain/chat/types';
import type { ConversationDraft } from '@domain/conversation/draft';
import { conversationDraftChecklist } from '@domain/conversation/draft';
import { UserAvatar } from '@shared/ui/UserAvatar';
import { videoCallStatus } from '../components/videoCallPresentation';
import { useVideoTrackAvailability } from '../hooks/useVideoTrackAvailability';
import {
  Modal,
  Stack,
  Group,
  Text,
  Paper,
  Button,
  ActionIcon,
  Box,
  TextInput,
  Textarea,
  ScrollArea,
  Checkbox,
  Title,
  Center,
  Badge,
} from '@mantine/core';
import {
  IconArrowsMaximize,
  IconArrowsMinimize,
  IconMessageCircle,
  IconMicrophone,
  IconMicrophoneOff,
  IconPhone,
  IconPhoneOff,
  IconVideo,
  IconVideoOff,
  IconX,
  IconUsers,
  IconUserPlus,
  IconClipboardList,
  IconSettings,
  IconScreenShare,
  IconCameraRotate,
} from '@tabler/icons-react';

const callPulseKeyframes = `
@keyframes locs-pulse-ring {
  0% {
    box-shadow: 0 0 0 0 rgba(185, 87, 54, 0.65);
  }
  70% {
    box-shadow: 0 0 0 28px rgba(185, 87, 54, 0);
  }
  100% {
    box-shadow: 0 0 0 0 rgba(185, 87, 54, 0);
  }
}
@keyframes locs-pulse-speaking {
  0% {
    box-shadow: 0 0 0 0 rgba(48, 210, 124, 0.75);
  }
  70% {
    box-shadow: 0 0 0 28px rgba(48, 210, 124, 0);
  }
  100% {
    box-shadow: 0 0 0 0 rgba(48, 210, 124, 0);
  }
}
`;

type CreateGroupDraft = {
  name: string;
  memberIds: number[];
};

type CallAudioLevels = {
  local: number;
  remote: number;
  localSpeaking: boolean;
  remoteSpeaking: boolean;
};

function callStateLabel(callUiState: CallUiState) {
  if (callUiState === 'dialing') return '正在呼叫';
  if (callUiState === 'incoming') return '收到通话邀请';
  if (callUiState === 'connecting') return '正在建立连接';
  if (callUiState === 'error') return '连接失败';
  if (callUiState === 'ended') return '通话已结束';
  return '通话中';
}

function callStateDescription(callUiState: CallUiState, type: 'audio' | 'video') {
  if (callUiState === 'dialing') return type === 'audio' ? '等待对方接听语音通话' : '等待对方加入视频通话';
  if (callUiState === 'incoming') return type === 'audio' ? '对方正在等待你接听' : '对方邀请你加入视频通话';
  if (callUiState === 'connecting') return type === 'audio' ? '正在连接语音通道' : '正在连接视频画面';
  if (callUiState === 'error') return type === 'audio' ? '语音通道连接失败' : '视频通道连接失败';
  if (callUiState === 'ended') return '通话已结束';
  return type === 'audio' ? '语音通话进行中' : '视频通话进行中';
}

function AudioIncomingCallCard({
  incomingCall,
  callUiState,
  handleRespondIncomingCall,
}: {
  incomingCall: CallSession;
  callUiState?: CallUiState;
  handleRespondIncomingCall: (accepted: boolean) => void;
}) {
  return (
    <Box
      pos="fixed"
      inset={0}
      style={{
        zIndex: 400,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: 'rgba(0,0,0,0.55)',
        backdropFilter: 'blur(12px)',
      }}
    >
      <Paper
        w={400}
        maw="92vw"
        radius="lg"
        p="xl"
        style={{
          background: 'linear-gradient(155deg, #1B120B 0%, #2E1B10 50%, #22140D 100%)',
          border: '1px solid rgba(255, 255, 255, 0.16)',
          boxShadow: '0 24px 64px rgba(0,0,0,0.7), 0 4px 16px rgba(0,0,0,0.4)',
        }}
      >
        <Text size="xs" fw={800} c="var(--mantine-color-primary-filled)" tt="uppercase" lts={2} ta="center">
          语音来电邀请
        </Text>

        <Stack align="center" gap="md" my="lg">
          <Box
            style={{
              borderRadius: 999,
              animation: 'locs-pulse-ring 2.4s infinite cubic-bezier(0.45, 0, 0.55, 1)',
            }}
          >
            <UserAvatar
              id={incomingCall.peer.userId}
              name={incomingCall.peer.name}
              size={110}
              radius={55}
            />
          </Box>
          <Box ta="center">
            <Title order={2} c="white" fw={700} style={{ letterSpacing: '0.3px' }}>
              {incomingCall.peer.name}
            </Title>
            <Text c="rgba(255,255,255,0.7)" size="sm" mt={4}>
              {callUiState ? callStateDescription(callUiState, 'audio') : '等待你的接听…'}
            </Text>
          </Box>
        </Stack>

        <Group justify="center" gap={48} mt="xl" pb="xs">
          <Stack align="center" gap={6}>
            <ActionIcon
              aria-label="拒绝通话"
              color="red"
              bg="#FF3B30"
              size={60}
              radius={100}
              variant="filled"
              onClick={() => handleRespondIncomingCall(false)}
            >
              <IconPhoneOff size={26} />
            </ActionIcon>
            <Text size="xs" c="rgba(255,255,255,0.8)" fw={500}>
              拒绝
            </Text>
          </Stack>
          <Stack align="center" gap={6}>
            <ActionIcon
              aria-label="接听通话"
              color="green"
              bg="#30B566"
              size={60}
              radius={100}
              variant="filled"
              onClick={() => handleRespondIncomingCall(true)}
            >
              <IconPhone size={26} />
            </ActionIcon>
            <Text size="xs" c="rgba(255,255,255,0.8)" fw={500}>
              接听
            </Text>
          </Stack>
        </Group>
      </Paper>
    </Box>
  );
}

function VideoIncomingCallPanel({
  incomingCall,
  handleRespondIncomingCall,
}: {
  incomingCall: CallSession;
  handleRespondIncomingCall: (accepted: boolean) => void;
}) {
  return (
    <Box
      pos="fixed"
      inset={0}
      style={{ zIndex: 200, backgroundColor: 'rgba(0,0,0,0.85)', backdropFilter: 'blur(10px)' }}
    >
      <Stack h="100%" justify="center" align="center" p="xl">
        <Text size="xs" fw={700} c="var(--mantine-color-primary-filled)" tt="uppercase" lts={2}>视频来电邀请</Text>
        <Stack align="center" gap="md" mt={40}>
          <UserAvatar
            id={incomingCall.peer.userId}
            name={incomingCall.peer.name}
            size={120}
            radius={40}
          />
          <Box ta="center">
            <Title order={2} c="white">{incomingCall.peer.name}</Title>
            <Text c="gray.4">邀请你加入视频通话</Text>
          </Box>
        </Stack>
        <Group gap={40} mt={60}>
          <ActionIcon
            aria-label="拒绝视频通话"
            color="red"
            size={72}
            radius={100}
            variant="filled"
            onClick={() => handleRespondIncomingCall(false)}
          >
            <IconPhoneOff size={32} />
          </ActionIcon>
          <ActionIcon
            aria-label="接听视频通话"
            color="forestGreen"
            size={72}
            radius={100}
            variant="filled"
            onClick={() => handleRespondIncomingCall(true)}
          >
            <IconVideo size={32} />
          </ActionIcon>
        </Group>
      </Stack>
    </Box>
  );
}

function VoiceActivityBars({
  level,
  active,
}: {
  level: number;
  active: boolean;
}) {
  const normalizedLevel = Math.max(0, Math.min(1, level));
  const heights = [
    8 + normalizedLevel * 12,
    12 + normalizedLevel * 16,
    9 + normalizedLevel * 14,
  ];
  return (
    <Group gap={3} align="flex-end" h={28} aria-label={active ? '正在说话' : '未检测到说话'}>
      {heights.map((height, index) => (
        <Box
          key={index}
          w={4}
          h={height}
          style={{
            borderRadius: 999,
            backgroundColor: active ? 'var(--mantine-color-forestGreen-6)' : 'rgba(120,120,120,0.45)',
            transition: 'height 140ms ease, background-color 140ms ease',
          }}
        />
      ))}
    </Group>
  );
}

function VoiceActivityChip({
  label,
  level,
  active,
  dark = false,
  showLabel = false,
}: {
  label: string;
  level: number;
  active: boolean;
  dark?: boolean;
  showLabel?: boolean;
}) {
  return (
    <Group
      gap={8}
      px={10}
      py={6}
      wrap="nowrap"
      aria-label={active ? `${label} 正在说话` : label}
      style={{
        borderRadius: 999,
        border: `1px solid ${active ? 'var(--mantine-color-forestGreen-5)' : dark ? 'rgba(255,255,255,0.24)' : 'var(--locs-border-faint)'}`,
        backgroundColor: dark ? 'rgba(0,0,0,0.46)' : 'rgba(255,255,255,0.76)',
      }}
    >
      <VoiceActivityBars level={level} active={active} />
      {showLabel && (
        <Text size="xs" fw={800} c={dark ? 'white' : undefined} maw={120} truncate>
          {label}
        </Text>
      )}
    </Group>
  );
}

function formatCallDuration(seconds: number) {
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  const s = seconds % 60;
  if (h > 0) {
    return `${h.toString().padStart(2, '0')}:${m.toString().padStart(2, '0')}:${s.toString().padStart(2, '0')}`;
  }
  return `${m.toString().padStart(2, '0')}:${s.toString().padStart(2, '0')}`;
}

function AudioActiveCallCard({
  activeCall,
  isMuted = false,
  callUiState,
  callAudioLevels,
  remoteCallMediaRef,
  localCallMediaRef,
  handleHangupCall,
  handleToggleMute,
}: {
  activeCall: CallSession;
  isMuted?: boolean;
  callUiState: CallUiState;
  callAudioLevels: CallAudioLevels;
  remoteCallMediaRef: MutableRefObject<HTMLMediaElement | null>;
  localCallMediaRef: MutableRefObject<HTMLMediaElement | null>;
  handleHangupCall: () => void;
  handleToggleMute?: () => void;
}) {
  const [elapsedSeconds, setElapsedSeconds] = useState(0);

  useEffect(() => {
    if (callUiState !== 'connected') {
      setElapsedSeconds(0);
      return;
    }
    const timer = window.setInterval(() => {
      setElapsedSeconds((prev) => prev + 1);
    }, 1000);
    return () => {
      window.clearInterval(timer);
    };
  }, [callUiState]);

  return (
    <Box
      pos="fixed"
      inset={0}
      style={{
        zIndex: 400,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: 'rgba(0,0,0,0.55)',
        backdropFilter: 'blur(12px)',
      }}
    >
      <style>{callPulseKeyframes}</style>
      <audio
        ref={(node) => {
          remoteCallMediaRef.current = node;
        }}
        autoPlay
        playsInline
      />
      <audio
        ref={(node) => {
          localCallMediaRef.current = node;
        }}
        autoPlay
        playsInline
        muted
      />
      <Paper
        w={420}
        maw="92vw"
        radius="lg"
        p="xl"
        style={{
          background: 'linear-gradient(155deg, #1B120B 0%, #2E1B10 50%, #22140D 100%)',
          border: '1px solid rgba(255, 255, 255, 0.16)',
          boxShadow: '0 24px 64px rgba(0,0,0,0.7), 0 4px 16px rgba(0,0,0,0.4)',
        }}
      >
        <Text size="xs" fw={800} c="var(--mantine-color-primary-filled)" tt="uppercase" lts={2} ta="center">
          {callStateLabel(callUiState)}
        </Text>

        <Stack align="center" gap="md" my="lg">
          <Box
            style={{
              borderRadius: 999,
              animation: callAudioLevels.remoteSpeaking
                ? 'locs-pulse-speaking 1.6s infinite ease-out'
                : 'locs-pulse-ring 3s infinite cubic-bezier(0.45, 0, 0.55, 1)',
            }}
          >
            <UserAvatar
              id={activeCall.peer.userId}
              name={activeCall.peer.name}
              size={115}
              radius={60}
            />
          </Box>
          <Box ta="center">
            <Title order={2} c="white" fw={700} style={{ letterSpacing: '0.3px' }}>
              {activeCall.peer.name}
            </Title>
            <Group gap={6} justify="center" mt={4}>
              {callUiState === 'connected' && (
                <Group gap={2} align="flex-end" h={12}>
                  <Box w={2} h={4} bg="#30D27C" style={{ borderRadius: 1 }} />
                  <Box w={2} h={8} bg="#30D27C" style={{ borderRadius: 1 }} />
                  <Box w={2} h={12} bg="#30D27C" style={{ borderRadius: 1 }} />
                </Group>
              )}
              <Text
                c="rgba(255,255,255,0.7)"
                size="sm"
                style={{
                  fontVariantNumeric: 'tabular-nums',
                  letterSpacing: '0.4px',
                  fontWeight: 500,
                }}
              >
                {callUiState === 'connected'
                  ? formatCallDuration(elapsedSeconds)
                  : callStateDescription(callUiState, 'audio')}
              </Text>
            </Group>
          </Box>
        </Stack>

        <Group justify="center" gap={48} mt="xl" pb="xs">
          {handleToggleMute && (
            <Stack align="center" gap={6}>
              <ActionIcon
                aria-label={isMuted ? '取消静音' : '静音'}
                size={58}
                radius={100}
                variant="filled"
                bg={isMuted ? '#b95736' : 'rgba(255, 255, 255, 0.16)'}
                c="white"
                style={{
                  backdropFilter: 'blur(8px)',
                  border: isMuted ? '1px solid #b95736' : '1px solid rgba(255, 255, 255, 0.1)',
                }}
                onClick={handleToggleMute}
              >
                {isMuted ? <IconMicrophoneOff size={26} /> : <IconMicrophone size={26} />}
              </ActionIcon>
              <Text size="xs" c="rgba(255,255,255,0.8)" fw={500}>
                {isMuted ? '已静音' : '静音'}
              </Text>
            </Stack>
          )}

          <Stack align="center" gap={6}>
            <ActionIcon
              aria-label="挂断"
              size={58}
              radius={100}
              variant="filled"
              color="red"
              bg="#FF3B30"
              c="white"
              onClick={handleHangupCall}
            >
              <IconPhoneOff size={26} />
            </ActionIcon>
            <Text size="xs" c="rgba(255,255,255,0.8)" fw={500}>
              挂断
            </Text>
          </Stack>
        </Group>
      </Paper>
    </Box>
  );
}

function VideoActiveCallStage({
  activeCall,
  isMuted = false,
  isVideoEnabled = true,
  callUiState,
  callAudioLevels: _callAudioLevels,
  remoteCallMediaRef,
  localCallMediaRef,
  remoteCallStream,
  localCallStream,
  handleHangupCall,
  handleToggleMute,
  handleToggleVideo,
  openSession: _openSession,
}: {
  activeCall: CallSession;
  isMuted?: boolean;
  isVideoEnabled?: boolean;
  callUiState: CallUiState;
  callAudioLevels: CallAudioLevels;
  remoteCallMediaRef: MutableRefObject<HTMLMediaElement | null>;
  localCallMediaRef: MutableRefObject<HTMLMediaElement | null>;
  remoteCallStream: MediaStream | null;
  localCallStream: MediaStream | null;
  handleHangupCall: () => void;
  handleToggleMute?: () => void;
  handleToggleVideo?: () => void;
  openSession: (sessionKey: string) => void;
}) {
  const [isLocalPip, setIsLocalPip] = useState(true);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [mainHeight, setMainHeight] = useState(360);
  const [elapsedSeconds, setElapsedSeconds] = useState(0);

  const [position, setPosition] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const dragStartRef = useRef({ x: 0, y: 0 });

  useEffect(() => {
    if (callUiState !== 'connected') {
      setElapsedSeconds(0);
      return;
    }
    const timer = window.setInterval(() => {
      setElapsedSeconds((prev) => prev + 1);
    }, 1000);
    return () => {
      window.clearInterval(timer);
    };
  }, [callUiState]);

  const handlePointerDown = (e: React.PointerEvent<HTMLDivElement>) => {
    if (isFullscreen) return;
    const target = e.target as HTMLElement;
    if (
      target.closest('button') ||
      target.closest('a') ||
      target.closest('.pip-video-container')
    ) {
      return;
    }
    setIsDragging(true);
    dragStartRef.current = {
      x: e.clientX - position.x,
      y: e.clientY - position.y,
    };
    target.setPointerCapture(e.pointerId);
  };

  const handlePointerMove = (e: React.PointerEvent<HTMLDivElement>) => {
    if (!isDragging) return;
    const nextX = e.clientX - dragStartRef.current.x;
    const nextY = e.clientY - dragStartRef.current.y;
    setPosition({ x: nextX, y: nextY });
  };

  const handlePointerUp = (e: React.PointerEvent<HTMLDivElement>) => {
    if (!isDragging) return;
    setIsDragging(false);
    const target = e.target as HTMLElement;
    target.releasePointerCapture(e.pointerId);
  };

  useEffect(() => {
    const mainVideo = (isLocalPip ? remoteCallMediaRef.current : localCallMediaRef.current) as HTMLVideoElement | null;
    if (!mainVideo) {
      setMainHeight(360);
      return;
    }

    const updateHeight = () => {
      if (mainVideo.videoWidth > 0 && mainVideo.videoHeight > 0) {
        const calculatedHeight = 480 * (mainVideo.videoHeight / mainVideo.videoWidth);
        const clampedHeight = Math.max(360, Math.min(480, calculatedHeight));
        setMainHeight(clampedHeight);
      } else {
        setMainHeight(360);
      }
    };

    updateHeight();
    mainVideo.addEventListener('loadedmetadata', updateHeight);
    mainVideo.addEventListener('resize', updateHeight);

    return () => {
      mainVideo.removeEventListener('loadedmetadata', updateHeight);
      mainVideo.removeEventListener('resize', updateHeight);
    };
  }, [isLocalPip, localCallStream, remoteCallStream, callUiState, isVideoEnabled, localCallMediaRef, remoteCallMediaRef]);

  const remoteVideoAvailable = useVideoTrackAvailability(remoteCallStream);
  const localVideoAvailable = useVideoTrackAvailability(localCallStream);
  const hasRemoteVideo = callUiState === 'connected' && remoteVideoAvailable;
  const hasLocalVideo = isVideoEnabled && localVideoAvailable;
  const localOnMain = !isLocalPip && hasRemoteVideo && hasLocalVideo;

  // Keep both media elements mounted: the remote element also carries audio.
  useEffect(() => {
    for (const [element, stream] of [
      [remoteCallMediaRef.current, remoteCallStream],
      [localCallMediaRef.current, localCallStream],
    ] as const) {
      if (!element) continue;
      element.srcObject = stream;
      if (stream) void element.play().catch(() => undefined);
    }
  }, [localOnMain, hasLocalVideo, localCallStream, remoteCallStream, localCallMediaRef, remoteCallMediaRef]);

  const stageStyles = isFullscreen
    ? { position: 'fixed' as const, inset: 0, zIndex: 300, backgroundColor: '#000' }
    : {
        position: 'fixed' as const,
        right: 24,
        bottom: 24,
        width: 500,
        maxWidth: '92vw',
        zIndex: 150,
        transform: `translate(${position.x}px, ${position.y}px)`,
        touchAction: 'none',
      };

  return (
    <Box
      style={{
        ...stageStyles,
        borderRadius: isFullscreen ? 0 : 20,
        overflow: 'hidden',
        border: 'none',
        boxShadow: isFullscreen ? 'none' : '0 24px 60px rgba(0,0,0,0.65)',
        cursor: isFullscreen ? 'default' : isDragging ? 'grabbing' : 'grab',
      }}
      onPointerDown={handlePointerDown}
      onPointerMove={handlePointerMove}
      onPointerUp={handlePointerUp}
      onPointerCancel={handlePointerUp}
    >
      <Box
        pos="relative"
        style={{
          height: isFullscreen ? '100vh' : mainHeight,
          minHeight: 360,
          transition: isFullscreen ? 'none' : 'height 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
          backgroundColor: '#111',
          overflow: 'hidden',
        }}
      >
          <Box
            component="video"
            ref={(node) => {
              (localOnMain ? localCallMediaRef : remoteCallMediaRef).current = node;
            }}
            autoPlay
            playsInline
            muted={localOnMain}
            w="100%"
            h="100%"
            style={{ objectFit: 'cover', visibility: hasRemoteVideo ? 'visible' : 'hidden' }}
          />
        {!hasRemoteVideo && (
          <VideoCallMediaPlaceholder call={activeCall} callUiState={callUiState} />
        )}

        <Box
          className="pip-video-container"
          pos="absolute"
          right={16}
          bottom={100}
          w={110}
          style={{
            aspectRatio: '9 / 12',
            borderRadius: 18,
            overflow: 'hidden',
            border: 'none',
            boxShadow: '0 8px 24px rgba(0,0,0,0.5)',
            cursor: 'pointer',
            zIndex: 10,
            visibility: hasLocalVideo ? 'visible' : 'hidden',
          }}
          onClick={() => { if (hasRemoteVideo) setIsLocalPip(!isLocalPip); }}
        >
          <Box
            component="video"
            ref={(node) => {
              (localOnMain ? remoteCallMediaRef : localCallMediaRef).current = node;
            }}
            autoPlay
            playsInline
            muted={!localOnMain}
            w="100%"
            h="100%"
            style={{ objectFit: 'cover' }}
          />
        </Box>

        <Box
          pos="absolute"
          top={0}
          left={0}
          right={0}
          h={120}
          style={{
            background: 'linear-gradient(to bottom, rgba(0, 0, 0, 0.8) 0%, rgba(0, 0, 0, 0) 100%)',
            pointerEvents: 'none',
            zIndex: 2,
          }}
        />

        <Box
          pos="absolute"
          bottom={0}
          left={0}
          right={0}
          h={140}
          style={{
            background: 'linear-gradient(to top, rgba(0, 0, 0, 0.85) 0%, rgba(0, 0, 0, 0) 100%)',
            pointerEvents: 'none',
            zIndex: 2,
          }}
        />

        <Stack pos="absolute" inset={0} p="md" justify="space-between" style={{ pointerEvents: 'none', zIndex: 3 }}>
          <Group justify="space-between" align="center" style={{ pointerEvents: 'auto' }}>
            <Box w={28} aria-hidden="true" />
            {hasRemoteVideo ? <Stack align="center" gap={2} maw="calc(100% - 80px)">
              <Text fw={700} c="white" size="md" lineClamp={1} style={{ textShadow: '0 2px 4px rgba(0,0,0,0.6)' }}>
                {activeCall.peer.name}
              </Text>
              <Group gap={4} justify="center">
                {callUiState === 'connected' && (
                  <Group gap={2} align="flex-end" h={10}>
                    <Box w={2} h={3} bg="#30D27C" style={{ borderRadius: 1 }} />
                    <Box w={2} h={6} bg="#30D27C" style={{ borderRadius: 1 }} />
                    <Box w={2} h={10} bg="#30D27C" style={{ borderRadius: 1 }} />
                  </Group>
                )}
                <Text
                  size="xs"
                  c="rgba(255,255,255,0.75)"
                  fw={600}
                  style={{ fontVariantNumeric: 'tabular-nums' }}
                >
                  {callUiState === 'connected' ? formatCallDuration(elapsedSeconds) : videoCallStatus(callUiState)}
                </Text>
              </Group>
            </Stack> : <Box />}
            <ActionIcon
              aria-label={isFullscreen ? '退出全屏' : '全屏显示通话'}
              variant="filled"
              bg="rgba(0,0,0,0.4)"
              radius="xl"
              size="md"
              onClick={() => setIsFullscreen(!isFullscreen)}
            >
              {isFullscreen ? <IconArrowsMinimize size={16} /> : <IconArrowsMaximize size={16} />}
            </ActionIcon>
          </Group>

          <Group justify="space-evenly" style={{ pointerEvents: 'auto' }} pb="xs">
            <Stack align="center" gap={4}>
              <ActionIcon
                aria-label="画面切换"
                size={50}
                radius={100}
                variant="filled"
                bg="rgba(255, 255, 255, 0.2)"
                c="white"
                style={{ backdropFilter: 'blur(8px)' }}
                disabled={!hasRemoteVideo || !hasLocalVideo}
                onClick={() => setIsLocalPip(!isLocalPip)}
              >
                <IconCameraRotate size={22} />
              </ActionIcon>
              <Text size="xs" c="white" fw={500}>
                切换
              </Text>
            </Stack>

            <Stack align="center" gap={4}>
              <ActionIcon
                aria-label={isVideoEnabled ? '关闭摄像头' : '开启摄像头'}
                size={50}
                radius={100}
                variant="filled"
                bg={isVideoEnabled ? 'white' : 'rgba(255, 255, 255, 0.2)'}
                c={isVideoEnabled ? 'black' : 'white'}
                style={{ backdropFilter: 'blur(8px)' }}
                onClick={handleToggleVideo}
              >
                {isVideoEnabled ? <IconVideo size={22} /> : <IconVideoOff size={22} />}
              </ActionIcon>
              <Text size="xs" c="white" fw={500}>
                {isVideoEnabled ? '摄像头开' : '摄像头关'}
              </Text>
            </Stack>

            <Stack align="center" gap={4}>
              <ActionIcon
                aria-label={isMuted ? '取消静音' : '静音'}
                size={50}
                radius={100}
                variant="filled"
                bg={isMuted ? '#b95736' : 'rgba(255, 255, 255, 0.2)'}
                c="white"
                style={{
                  backdropFilter: 'blur(8px)',
                  border: isMuted ? '1px solid #b95736' : undefined,
                }}
                onClick={handleToggleMute}
              >
                {isMuted ? <IconMicrophoneOff size={22} /> : <IconMicrophone size={22} />}
              </ActionIcon>
              <Text size="xs" c="white" fw={500}>
                {isMuted ? '已静音' : '静音'}
              </Text>
            </Stack>

            <Stack align="center" gap={4}>
              <ActionIcon
                aria-label="挂断"
                size={50}
                radius={100}
                variant="filled"
                color="red"
                bg="#FF3B30"
                c="white"
                onClick={handleHangupCall}
              >
                <IconPhoneOff size={22} />
              </ActionIcon>
              <Text size="xs" c="white" fw={500}>
                挂断
              </Text>
            </Stack>
          </Group>
        </Stack>
      </Box>
    </Box>
  );
}

function VideoCallMediaPlaceholder({
  call,
  callUiState,
}: {
  call: CallSession;
  callUiState: CallUiState;
}) {
  const status = videoCallStatus(callUiState);

  return (
    <Center pos="absolute" top={56} bottom={100} left={16} right={16}>
      <Stack align="center" gap={8} w="100%">
        <UserAvatar id={call.peer.userId} name={call.peer.name} size={80} radius={40} />
        <Text c="white" fw={700} size="lg" maw="100%" lineClamp={1}>{call.peer.name}</Text>
        <Text c="rgba(255,255,255,0.72)" size="sm" role="status" aria-live="polite">{status}</Text>
      </Stack>
    </Center>
  );
}

type WorkspaceOverlaysProps = {
  incomingCall: CallSession | null;
  activeCall: CallSession | null;
  isMuted?: boolean;
  isVideoEnabled?: boolean;
  callAudioLevels: CallAudioLevels;
  callUiState: CallUiState;
  callConnectionState: CallConnectionState;
  remoteCallStream: MediaStream | null;
  localCallStream: MediaStream | null;
  remoteCallMediaRef: MutableRefObject<HTMLMediaElement | null>;
  localCallMediaRef: MutableRefObject<HTMLMediaElement | null>;
  handleRespondIncomingCall: (accepted: boolean) => void;
  handleHangupCall: () => void;
  handleToggleMute?: () => void;
  handleToggleVideo?: () => void;
  openSession: (sessionKey: string) => void;
  callBootstrapError: string | null;
  isCreateGroupModalOpen: boolean;
  setIsCreateGroupModalOpen: React.Dispatch<React.SetStateAction<boolean>>;
  createGroupSubmitting: boolean;
  createGroupDraft: CreateGroupDraft;
  setCreateGroupDraft: React.Dispatch<React.SetStateAction<CreateGroupDraft>>;
  contacts: ContactDirectoryUser[];
  handleToggleCreateGroupMember: (memberId: number) => void;
  handleCreateGroup: () => Promise<void>;
  isFriendRequestModalOpen: boolean;
  setIsFriendRequestModalOpen: React.Dispatch<React.SetStateAction<boolean>>;
  friendRequestTarget: UserSearchResult | null;
  friendRequestNote: string;
  setFriendRequestNote: React.Dispatch<React.SetStateAction<string>>;
  handleSendFriendRequest: (targetUserId: number, note: string) => Promise<void>;
  sendingRequestByUserId: Record<number, boolean>;
  conversationDraftTarget: ContactDirectoryUser | null;
  conversationDraft: ConversationDraft;
  setConversationDraft: React.Dispatch<React.SetStateAction<ConversationDraft>>;
  closeConversationDraft: () => void;
  submitConversationDraft: () => Promise<void>;
  conversationDraftSubmitting: boolean;
};

export function WorkspaceOverlays({
  incomingCall,
  activeCall,
  isMuted,
  isVideoEnabled,
  callAudioLevels,
  callUiState,
  remoteCallStream,
  localCallStream,
  remoteCallMediaRef,
  localCallMediaRef,
  handleRespondIncomingCall,
  handleHangupCall,
  handleToggleMute,
  handleToggleVideo,
  openSession,
  callBootstrapError,
  isCreateGroupModalOpen,
  setIsCreateGroupModalOpen,
  createGroupSubmitting,
  createGroupDraft,
  setCreateGroupDraft,
  contacts,
  handleToggleCreateGroupMember,
  handleCreateGroup,
  isFriendRequestModalOpen,
  setIsFriendRequestModalOpen,
  friendRequestTarget,
  friendRequestNote,
  setFriendRequestNote,
  handleSendFriendRequest,
  sendingRequestByUserId,
  conversationDraftTarget,
  conversationDraft,
  setConversationDraft,
  closeConversationDraft,
  submitConversationDraft,
  conversationDraftSubmitting,
}: WorkspaceOverlaysProps) {
  return (
    <>
      <style>{callPulseKeyframes}</style>

      {/* Audio Call Overlay */}
      {incomingCall?.type === 'audio' ? (
        <AudioIncomingCallCard
          incomingCall={incomingCall}
          callUiState={callUiState}
          handleRespondIncomingCall={handleRespondIncomingCall}
        />
      ) : activeCall?.type === 'audio' ? (
        <AudioActiveCallCard
          activeCall={activeCall}
          isMuted={isMuted}
          callUiState={callUiState}
          callAudioLevels={callAudioLevels}
          remoteCallMediaRef={remoteCallMediaRef}
          localCallMediaRef={localCallMediaRef}
          handleHangupCall={handleHangupCall}
          handleToggleMute={handleToggleMute}
        />
      ) : null}

      {/* Video Incoming Overlay */}
      {incomingCall?.type === 'video' && (
        <VideoIncomingCallPanel
          incomingCall={incomingCall}
          handleRespondIncomingCall={handleRespondIncomingCall}
        />
      )}

      {/* Video Active Overlay */}
      {activeCall?.type === 'video' && (
        <VideoActiveCallStage
          activeCall={activeCall}
          isMuted={isMuted}
          isVideoEnabled={isVideoEnabled}
          remoteCallStream={remoteCallStream}
          localCallStream={localCallStream}
          callUiState={callUiState}
          callAudioLevels={callAudioLevels}
          remoteCallMediaRef={remoteCallMediaRef}
          localCallMediaRef={localCallMediaRef}
          handleHangupCall={handleHangupCall}
          handleToggleMute={handleToggleMute}
          handleToggleVideo={handleToggleVideo}
          openSession={openSession}
        />
      )}

      {/* Call Error Toast */}
      {callBootstrapError && (
        <Paper
          pos="fixed"
          bottom={20}
          left="50%"
          p="sm"
          radius="md"
          withBorder
          shadow="md"
          style={{ zIndex: 500, transform: 'translateX(-50%)', backgroundColor: 'var(--mantine-color-red-0)', borderColor: 'var(--mantine-color-red-2)' }}
        >
          <Group gap="xs">
            <Text size="sm" fw={700} c="red">通话提示:</Text>
            <Text size="sm">{callBootstrapError}</Text>
          </Group>
        </Paper>
      )}

      {/* Create Hub Modal */}
      <Modal
        opened={isCreateGroupModalOpen}
        onClose={() => !createGroupSubmitting && setIsCreateGroupModalOpen(false)}
        title={<Text fw={900}>创建群组</Text>}
        size="md"
        centered
        radius="lg"
        padding="lg"
      >
        <Stack gap="lg">
          <Paper
            p="md"
            radius="md"
            style={{
              background: 'linear-gradient(135deg, rgba(196, 90, 60, 0.12), rgba(237, 246, 241, 0.9))',
              border: '1px solid var(--locs-border-faint)',
            }}
          >
            <Group gap="sm" wrap="nowrap">
              <Center w={42} h={42} c="terracotta.7" style={{ borderRadius: 14, backgroundColor: 'rgba(255,255,255,0.72)' }}>
                <IconUsers size={22} stroke={2.2} />
              </Center>
              <Stack gap={2} style={{ minWidth: 0 }}>
                <Text fw={850} size="sm">创建一个专属话题群组，邀请联系人加入讨论</Text>
                <Text size="xs" c="dimmed">目前支持邀请已有联系人加入。</Text>
              </Stack>
            </Group>
          </Paper>
          <TextInput
            label="群组名称"
            placeholder="例如：产品讨论组"
            value={createGroupDraft.name}
            onChange={(event) => {
              const value = event.currentTarget.value;
              setCreateGroupDraft((prev) => ({ ...prev, name: value }));
            }}
            disabled={createGroupSubmitting}
            required
          />
          <Box>
            <Group justify="space-between" mb="xs">
              <Text fw={700} size="sm">选择成员</Text>
              <Text size="xs" c="dimmed">{createGroupDraft.memberIds.length} 已选</Text>
            </Group>
            <Paper withBorder radius="md" p={0} style={{ overflow: 'hidden' }}>
              <ScrollArea h={240} type="scroll">
                <Stack gap={0}>
                  {contacts.length === 0 ? (
                    <Box py={40} ta="center">
                      <Text size="sm" fw={700}>还没有可邀请的联系人</Text>
                      <Text size="xs" c="dimmed" mt={4}>先添加联系人，再创建群组。</Text>
                    </Box>
                  ) : (
                    contacts.map((contact, index) => {
                      const checked = createGroupDraft.memberIds.includes(contact.id);
                      return (
                        <Box
                          key={contact.id}
                          component="label"
                          p="sm"
                          style={{
                            cursor: createGroupSubmitting ? 'not-allowed' : 'pointer',
                            borderBottom: index === contacts.length - 1 ? 'none' : '1px solid var(--locs-border)',
                            backgroundColor: checked ? 'var(--mantine-color-terracotta-0)' : 'transparent',
                          }}
                        >
                          <Group justify="space-between" wrap="nowrap">
                            <Group gap="sm" style={{ flex: 1, minWidth: 0 }}>
                              <UserAvatar id={contact.id} name={contact.name} avatarUrl={contact.avatarUrl} size={32} />
                              <Stack gap={0} style={{ flex: 1, minWidth: 0 }}>
                                <Text size="sm" fw={600} truncate>{contact.name}</Text>
	                                <Text size="xs" c="dimmed" truncate>{contact.signInfo || '平台用户'}</Text>
                              </Stack>
                            </Group>
                            <Checkbox
                              checked={checked}
                              onChange={() => handleToggleCreateGroupMember(contact.id)}
                              disabled={createGroupSubmitting}
                              radius="md"
                            />
                          </Group>
                        </Box>
                      );
                    })
                  )}
                </Stack>
              </ScrollArea>
            </Paper>
          </Box>
          <Group justify="flex-end" gap="sm">
            <Button variant="default" onClick={() => setIsCreateGroupModalOpen(false)} disabled={createGroupSubmitting}>
              取消
            </Button>
            <Button
              onClick={() => void handleCreateGroup()}
              loading={createGroupSubmitting}
              disabled={!createGroupDraft.name.trim() || createGroupDraft.memberIds.length === 0}
              leftSection={<IconUsers size={16} />}
            >
              创建群组
            </Button>
          </Group>
        </Stack>
      </Modal>

      {/* Relationship Request Modal */}
      <Modal
        opened={isFriendRequestModalOpen}
        onClose={() => setIsFriendRequestModalOpen(false)}
        title={<Text fw={900}>添加关系</Text>}
        size="sm"
        centered
        radius="lg"
        padding="lg"
      >
        {friendRequestTarget && (
          <Stack gap="lg">
            <Paper
              p="md"
              radius="md"
              style={{
                background: 'linear-gradient(135deg, rgba(253, 236, 232, 0.78), rgba(255,255,255,0.95))',
                border: '1px solid var(--locs-border-faint)',
              }}
            >
              <Group wrap="nowrap" gap="md">
                <UserAvatar id={friendRequestTarget.id} name={friendRequestTarget.name} avatarUrl={friendRequestTarget.avatarUrl} size={58} />
                <Stack gap={2} style={{ minWidth: 0 }}>
                  <Text fw={850} size="md" truncate>{friendRequestTarget.name || '平台用户'}</Text>
                  <Text size="xs" c="dimmed">添加好友后可以发起会话和文件传输</Text>
                </Stack>
              </Group>
            </Paper>
            <TextInput
              label="打招呼的话"
              placeholder="选填，例如：你好，希望和你建立联系"
              leftSection={<IconUserPlus size={16} stroke={2} />}
              value={friendRequestNote}
              onChange={(event) => setFriendRequestNote(event.currentTarget.value)}
            />
            <Group justify="flex-end" gap="sm">
              <Button variant="default" onClick={() => setIsFriendRequestModalOpen(false)}>
                取消
              </Button>
              <Button
                onClick={() => {
                  const targetId = friendRequestTarget.id;
                  setIsFriendRequestModalOpen(false);
                  void handleSendFriendRequest(targetId, friendRequestNote);
                }}
                loading={sendingRequestByUserId[friendRequestTarget.id]}
              >
                发送关系申请
              </Button>
            </Group>
          </Stack>
        )}
      </Modal>

      {/* Conversation Draft Modal */}
      <Modal
        opened={!!conversationDraftTarget}
        onClose={() => !conversationDraftSubmitting && closeConversationDraft()}
        title={<Text fw={900}>发起会话</Text>}
        size="lg"
        centered
        radius="lg"
        padding="lg"
      >
        {conversationDraftTarget && (
          <Stack gap="lg">
            <Paper
              p="md"
              radius="md"
              style={{
                background: 'linear-gradient(135deg, rgba(237, 246, 241, 0.95), rgba(253, 236, 232, 0.62))',
                border: '1px solid var(--locs-border-faint)',
              }}
            >
              <Group wrap="nowrap" gap="md" align="flex-start">
                <UserAvatar id={conversationDraftTarget.id} name={conversationDraftTarget.name} avatarUrl={conversationDraftTarget.avatarUrl} size={58} />
                <Stack gap={6} style={{ minWidth: 0, flex: 1 }}>
                  <Stack gap={1}>
                    <Text size="xs" fw={800} c="terracotta.7" tt="uppercase" lts={0.6}>会话说明</Text>
                    <Text fw={850} size="md" truncate>{conversationDraftTarget.name || '平台用户'}</Text>
                    <Text size="xs" c="dimmed">先填写关键信息，后续可继续补充图片、语音或文件。</Text>
                  </Stack>
                  <Group gap={6}>
                    {conversationDraftChecklist.map((item) => (
                      <Badge key={item} size="sm" radius="sm" variant="light" color="gray">
                        {item}
                      </Badge>
                    ))}
                  </Group>
                </Stack>
              </Group>
            </Paper>

            <Box>
              <Group gap="xs" mb="xs">
                <IconClipboardList size={16} stroke={2.2} color="var(--mantine-color-terracotta-7)" />
                <Text fw={800} size="sm">会话说明</Text>
              </Group>
              <Stack gap="sm">
                <TextInput
                  label="主题/事由"
                  placeholder="例如：技术方案讨论、产品反馈"
                  value={conversationDraft.topic}
                  onChange={(event) => {
                    const value = event.currentTarget.value;
                    setConversationDraft((prev) => ({ ...prev, topic: value }));
                  }}
                  disabled={conversationDraftSubmitting}
                />
                <Textarea
                  label="背景说明"
                  placeholder="例如：目前遇到了某个问题，已经尝试过……"
                  minRows={2}
                  value={conversationDraft.background}
                  onChange={(event) => {
                    const value = event.currentTarget.value;
                    setConversationDraft((prev) => ({ ...prev, background: value }));
                  }}
                  disabled={conversationDraftSubmitting}
                />
                <TextInput
                  label="期望结果"
                  placeholder="例如：希望得到建议方案或解决方案"
                  value={conversationDraft.expectation}
                  onChange={(event) => {
                    const value = event.currentTarget.value;
                    setConversationDraft((prev) => ({ ...prev, expectation: value }));
                  }}
                  disabled={conversationDraftSubmitting}
                />
                <Textarea
                  label="补充说明"
                  placeholder="例如：截止时间、优先级或其他注意事项"
                  minRows={2}
                  value={conversationDraft.note}
                  onChange={(event) => {
                    const value = event.currentTarget.value;
                    setConversationDraft((prev) => ({ ...prev, note: value }));
                  }}
                  disabled={conversationDraftSubmitting}
                />
              </Stack>
            </Box>

            <Group justify="flex-end" gap="sm">
              <Button variant="default" onClick={closeConversationDraft} disabled={conversationDraftSubmitting}>
                取消
              </Button>
              <Button
                onClick={() => void submitConversationDraft()}
                loading={conversationDraftSubmitting}
                leftSection={<IconMessageCircle size={16} />}
              >
                发送说明并进入会话
              </Button>
            </Group>
          </Stack>
        )}
      </Modal>
    </>
  );
}
